// /src/hooks/useUrlViewMode.js
// react
import { useCallback, useEffect, useMemo } from 'react';

// tanstack
import { useLocation } from '@tanstack/react-router';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

export default function useUrlViewMode({
  allowed = ['column', 'box', 'line'],
  fallback = 'column',
} = {}) {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  // Read from the real URL (source of truth)
  const viewMode = useMemo(() => {
    const sp = new URLSearchParams(location.searchStr);
    const v = sp.get('view');
    return allowed.includes(v) ? v : fallback;
  }, [location.searchStr, allowed, fallback]);

  // Always normalize when the URL changes (covers other hooks overwriting view)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const current = next.get('view');
        if (current === normalized) return prev; // Already normalized by another effect, nothing to do

        const normalized = allowed.includes(current) ? current : fallback;

        next.set('view', normalized);
        return next;
      },
      { replace: true },
    );
  }, [allowed, fallback, setSearchParams]);

  const setViewMode = useCallback(
    (next, { push = false } = {}) => {
      const safe = allowed.includes(next) ? next : fallback;

      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set('view', safe);
          return sp;
        },
        { replace: !push },
      );
    },
    [allowed, fallback, setSearchParams],
  );

  return [viewMode, setViewMode];
}
